/**
 * Freischalt-Stand der Baustein-Zeilen (Abschnitt E).
 * Klasse PORTAL_BAUSTEIN_FREISCHALTUNG.
 *
 * Grün nur, wenn dieser Sitz schon einen Schalter hat und der auf an steht:
 * - 1 Admin-Zugang → portal_role it_admin oder is_primary (wie canManageTeamAccess)
 * - Sitze / 2 Sitze extra → included_seats bzw. max_seats am Kunden, nie der Tarif-Default 5/10
 * - Litigation → customer.tier === premium (Fallanalyse-Nav). Die Live-View bleibt unangetastet.
 *
 * Kein Schalter → nicht grün. Kein Hardcode „alles an“. Keine Anker-Euro.
 */

export interface BausteinSeatInput {
  tier?: string | null;
  portalRole?: string | null;
  isPrimary?: boolean | null;
  includedSeats?: number | null;
  maxSeats?: number | null;
}

export type BausteinUnlockKind = 'on' | 'off' | 'no-switch';

export interface BausteinUnlockResult {
  kind: BausteinUnlockKind;
  /** Vorhandener Schalter. null = dieser Name hat keinen. */
  switchId: string | null;
  /** Report-Satz, nicht die Kundenfläche. */
  report: string;
}

export const BAUSTEIN_STATUS_ON = 'freigeschaltet';
export const BAUSTEIN_STATUS_OFF = 'nicht freigeschaltet';

/** Look der Akte. Grün bleibt der Freischalt-Punkt. Die Fläche zeigt Zeilen, keine Karten. */
export const BAUSTEIN_NAME_ON = 'font-semibold text-violet-100';
export const BAUSTEIN_NAME_OFF = 'font-semibold text-slate-200';
export const BAUSTEIN_STATUS_CLASS_ON = 'mt-1 text-sm font-medium text-emerald-300';
export const BAUSTEIN_STATUS_CLASS_OFF = 'mt-1 text-sm text-slate-500';
export const BAUSTEIN_DOT_ON = 'mr-2 inline-block h-2.5 w-2.5 rounded-full bg-emerald-400 align-middle';

const SWITCH_ADMIN = 'portal_role|is_primary';
const SWITCH_SEATS = 'included_seats|max_seats';
const SWITCH_TIER = 'customer.tier';

/** Namen ohne Sitz-/Kanzlei-Schalter. Gleicher Name in Modul und Muster teilt sich den Satz. */
export const BAUSTEIN_MISSING_SWITCH: Readonly<Record<string, string>> = {
  'Orbis-Plattform': 'kein Modul-Schalter am Sitz (Login ist die Hülle, kein Flag)',
  'Edge-Betrieb / Hosting': 'kein Feld Betrieb ARKORBIT vs Betrieb Kanzlei',
  'Backup / Updates': 'kein Modul-Schalter',
  'Basis-Support': 'kein Modul-Schalter',
  Diktat: 'kein Sitz-Schalter (Primärnav ohne Gate ist kein Kauf-Flag)',
  ARKO: 'kein Sitz-Schalter',
  'LEXCORE-Fach': 'kein Fach-Stack-Feld am Kunden',
  JUDICATURE: 'kein Sitz-Schalter (Snapshot-Env ist Build, kein Kanzlei-Kauf)',
  'Support Plus': 'kein Schalter; Schema: nicht verkaufen bis Mitarbeiter',
  'Betrieb ARKORBIT': 'kein Betriebsart-Feld',
  'Hosting-Zeile': 'kein Betriebsart-Feld',
  'Setup / Box': 'kein Modul-Schalter',
  Server: 'kein Betriebsart-Feld',
  'Betrieb Kanzlei': 'kein Betriebsart-Feld',
  'Setup und Abnahme': 'kein Modul-Schalter',
  'Basis Betrieb ARKORBIT': 'kein Betriebsart-Feld',
};

function positiveInt(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return Math.floor(value);
}

/**
 * Deckel wie seatCeiling, aber nur gesetzte Felder.
 * includedSeatsForTier (5/10) ist das alte Paketmodell und zählt hier nicht.
 */
export function explicitSeatCeiling(seat: BausteinSeatInput): number | null {
  const maxSeats = positiveInt(seat.maxSeats);
  if (maxSeats != null) return maxSeats;
  return positiveInt(seat.includedSeats);
}

/** Spiegel von canManageTeamAccess, ohne constants-Import. */
export function isBausteinAdminSeat(seat: BausteinSeatInput): boolean {
  if (seat.tier !== 'premium' && seat.tier !== 'standard') return false;
  if (seat.portalRole === 'member' || seat.portalRole === 'praktikant') return false;
  return seat.portalRole === 'it_admin' || seat.isPrimary === true;
}

function noSwitch(name: string): BausteinUnlockResult {
  return {
    kind: 'no-switch',
    switchId: null,
    report: BAUSTEIN_MISSING_SWITCH[name] ?? 'kein Schalter für diesen Namen',
  };
}

export function resolveBausteinUnlock(name: string, seat: BausteinSeatInput): BausteinUnlockResult {
  if (name === '1 Admin-Zugang') {
    const on = isBausteinAdminSeat(seat);
    return {
      kind: on ? 'on' : 'off',
      switchId: SWITCH_ADMIN,
      report: on
        ? 'Sitz ist Admin (portal_role it_admin oder is_primary)'
        : 'Schalter portal_role/is_primary steht an diesem Sitz auf aus',
    };
  }

  if (name === 'Sitze') {
    const ceiling = explicitSeatCeiling(seat);
    const on = ceiling != null && ceiling > 1;
    return {
      kind: on ? 'on' : 'off',
      switchId: SWITCH_SEATS,
      report: on
        ? `explizite Sitze ${ceiling} > 1 (included_seats/max_seats, kein Tarif-Default)`
        : ceiling == null
          ? 'included_seats und max_seats am Sitz leer — Extra-Sitze aus (Tarif-Default 5/10 zählt nicht)'
          : `explizite Sitze ${ceiling} — Modul Sitze aus (1 Admin bleibt Basis)`,
    };
  }

  if (name === '2 Sitze extra') {
    const ceiling = explicitSeatCeiling(seat);
    const extra = ceiling == null ? null : ceiling - 1;
    const on = extra != null && extra >= 2;
    return {
      kind: on ? 'on' : 'off',
      switchId: SWITCH_SEATS,
      report: on
        ? `explizite Extra-Sitze ${extra} >= 2`
        : 'Schalter included_seats/max_seats ergibt keine 2 Extra-Sitze (1 Admin abgezogen, kein Tarif-Default)',
    };
  }

  if (name === 'Litigation') {
    const on = seat.tier === 'premium';
    return {
      kind: on ? 'on' : 'off',
      switchId: SWITCH_TIER,
      report: on
        ? 'Fallanalyse-Gate tier=premium (Live-View unangetastet)'
        : 'Fallanalyse-Gate tier ist nicht premium',
    };
  }

  return noSwitch(name);
}

export function bausteinRowIsGreen(result: BausteinUnlockResult): boolean {
  return result.kind === 'on';
}
